import type { CountDto, CountListItemDto, MeDto, SkuDto } from '@pl/domain';
import { can, formatCode } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

/*
 * Ending a count without finishing it, from the office or a lead's phone (`POST /counts/:id/cancel`).
 * Nothing is recorded and no discrepancy is made; the count's slot, the phones' gate on earlier
 * results and the Wall's blank tiles are freed.
 */

/** "CAT5E CMR 1000ft White", or "pallet P3XW6-3FAXH": what a count is of, in words. */
export function countSubject(c: Pick<CountListItemDto, 'scope' | 'skuId' | 'palletCode'>, skuById: Map<string, SkuDto>): string {
  if (c.scope === 'pallet') return `pallet ${formatCode(c.palletCode ?? '')}`;
  const sku = c.skuId ? skuById.get(c.skuId) : undefined;
  return sku ? [sku.name, sku.colourName].filter(Boolean).join(' ') : 'this SKU';
}

/** How a canceled count reads, from who ended it. */
export const canceledWords = (c: Pick<CountDto, 'cancelledBy'>) => (c.cancelledBy === 'end_of_day' ? 'Canceled · end of day' : 'Canceled');

/** Who canceled a count, for the office: the lead by name, or "End of day" for the 03:00 sweep. Never the counter. */
export const canceledByWho = (c: Pick<CountDto, 'cancelledBy'> & { cancelledByName?: string | null }) =>
  c.cancelledBy === 'end_of_day' ? 'End of day' : (c.cancelledByName ?? 'A lead');

/** What the phone says of a count canceled under the person counting it, on the way back to the list. */
export const canceledNotice = (by: CountDto['cancelledBy']) =>
  by === 'end_of_day' ? 'This count was canceled at the end of the day.' : 'This count was canceled by a lead.';

/**
 * Whether this person may cancel a count at a site: a lead at one of their own sites, or an admin
 * anywhere. Cancel is offered nowhere else, in the office or on the phone.
 */
export const mayCancelAt = (me: MeDto | undefined, siteId: string) =>
  Boolean(me && can(me.role, 'resolve_discrepancy') && (me.siteIds === null || me.siteIds.includes(siteId)));

/** Whatever went wrong, in words a lead can act on. A count that ended meanwhile is the usual one. */
export function cancelErrorText(err: unknown, siteName: string): string | null {
  if (!err) return null;
  if (err instanceof ApiError && err.status === 409) return 'This count already ended';
  if (err instanceof ApiError && err.status === 403) return `This count is at ${siteName}. Only a lead there, or an admin, can cancel it.`;
  return err instanceof Error ? err.message : 'Could not cancel the count';
}

export function useCancelCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => api.post<CountDto>(`/counts/${id}/cancel`, reason?.trim() ? { reason: reason.trim() } : {}),
    // Asked for again either way: canceled now, or ended some other way meanwhile (the 409). A
    // canceled SKU is suggested again, and its codes are no longer hidden on Ship.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['counts'] });
      void qc.invalidateQueries({ queryKey: ['count'] });
      void qc.invalidateQueries({ queryKey: ['count-progress'] });
      void qc.invalidateQueries({ queryKey: ['counts-suggested'] });
      void qc.invalidateQueries({ queryKey: ['counts-counting'] });
    },
  });
}
